import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useOrganizationScopeOrNull } from '../components/OrganizationScope/context';
import {
  ACCOUNT_SCOPE_QUERY_KEY,
  getQueryOrganizationId,
} from '../configs/queryClient';
import { commonStrings } from '../constants/strings';
import type { TMe } from '../types/organization';

function errorMessage(error: unknown): string {
  const message =
    typeof error === 'string'
      ? error
      : (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message
    ? message
    : commonStrings.somethingWentWrong;
}

/** 利用者が始めた操作の結果トースト。別組織へ移った後に届いた結果は、開始した組織の名前を前置する。 */
export function useOrganizationToast() {
  const queryClient = useQueryClient();
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;

  const withOrigin = useCallback(
    (message: string) => {
      if (organizationId === null || organizationId === getQueryOrganizationId()) {
        return message;
      }
      // useMe は AuthContext（firebase）を読み込むため、同じ /me キャッシュを直接読む
      const organizationName = queryClient
        .getQueryData<TMe>([ACCOUNT_SCOPE_QUERY_KEY])
        ?.memberships.find((m) => m.organizationId === organizationId)
        ?.organizationName;
      return organizationName ? `【${organizationName}】${message}` : message;
    },
    [organizationId, queryClient],
  );

  const toastSuccess = useCallback(
    (message: string) => {
      toast.success(withOrigin(message));
    },
    [withOrigin],
  );

  const toastError = useCallback(
    (error: unknown) => {
      toast.error(withOrigin(errorMessage(error)));
    },
    [withOrigin],
  );

  return { toastSuccess, toastError };
}
